import _ from 'underscore';

export function toInteger(value: any): number {
  return parseInt(`${value}`, 10);
}

export function toString(value: any): string {
  return value !== undefined && value !== null ? `${value}` : '';
}

// used to eliminate floating point errors
export function toFloat(value: any, decimals: number = 2): number {
  return parseFloat(value.toFixed(decimals));
}

export function getValueInRange(value: number, max: number, min = 0): number {
  return Math.max(Math.min(value, max), min);
}

export function isString(value: any): value is string {
  return typeof value === 'string';
}

export function isNumber(value: any): boolean {
  return !isNaN(toInteger(value));
}

export function isInteger(value: any): value is number {
  return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
}

export function isDefined(value: any): boolean {
  return value !== undefined && value !== null;
}

export function padNumber(value: number) {
  if (isNumber(value)) {
    return `0${value}`.slice(-2);
  } else {
    return '';
  }
}

export function randomRange(min, max) {
  return Math.random() * (max - min) + min;
}

export function regExpEscape(text) {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

export function hasClassName(element: any, className: string): boolean {
  return (
    element &&
    element.className &&
    element.className.split &&
    element.className.split(/\s+/).indexOf(className) >= 0
  );
}

if (typeof Element !== 'undefined' && !Element.prototype.closest) {
  // Polyfill for ie10+

  if (!Element.prototype.matches) {
    // IE uses the non-standard name: msMatchesSelector
    Element.prototype.matches =
      (Element.prototype as any).msMatchesSelector || Element.prototype.webkitMatchesSelector;
  }

  Element.prototype.closest = function (s: string) {
    let el = this;
    if (!document.documentElement.contains(el)) {
      return null;
    }
    do {
      if (el.matches(s)) {
        return el;
      }
      el = el.parentElement || el.parentNode;
    } while (el !== null && el.nodeType === 1);
    return null;
  };
}

export function closest(element: HTMLElement, selector): HTMLElement {
  if (!selector) {
    return null;
  }

  return element.closest(selector);
}

// Iterate over all properties of object and sum them if they are numbers.
// If there are non number properties included, they will be recursed into.
export function mergeSum(obj1, obj2) {
  let result = {};

  [obj1, obj2].forEach(obj => {
    Object.keys(obj).forEach(key => {
      let val = obj[key];

      if (isNaN(val)) {
        if (result[key]) {
          //PERF we can shortcut if one or both objects are empty
          result[key] = mergeSum(result[key], val);
        } else {
          result[key] = val;
        }
      } else {
        if (result[key]) {
          result[key] += val;
        } else {
          result[key] = val;
        }
      }
    });
  });

  return result;
}

export function MapBy<Type>(items: Type[], selector: (item: Type) => any): Record<any, Type> {
  let map = {};
  items.forEach(item => {
    var val = selector(item);
    map[val] = item;
  });

  return map;
}

export function GroupBy<Type>(items: Type[], selector: (item: Type) => any): Record<any, Type[]> {
  let map: Record<any, Type[]> = {};
  items.forEach(item => {
    var val = selector(item);
    if (map[val] == null) {
      map[val] = [];
    }

    map[val].push(item);
  });

  return map;
}

export function DistinctBy<Type>(items: Type[], keySelector: (a: Type) => any): Type[] {
  var touched = {};
  return items.filter(function (item) {
    const key = keySelector(item);
    if (touched[key]) {
      return false;
    }

    touched[key] = true;
    return true;
  });
}

export function MaxBy<Type>(items: Type[], selector: (item: Type) => number): Type {
  return items.reduce((prev, current) => (selector(prev) > selector(current) ? prev : current));
}

export function OrderBy<Type>(items: Type[], selector: (item: Type) => number) {
  return items.sort((a, b) => selector(a) - selector(b));
}

export function ArrayEqualsIgnoreOrder(a: any[], b: any[]) {
  return (
    a.length == b.length &&
    a.every(i => b.some(j => _.isEqual(i, j))) &&
    b.every(i => a.some(j => _.isEqual(i, j)))
  );
}
